/**
 * Side-chat composer settings (Fast / Deep, Web), remembered per user in
 * localStorage. Sent to partnr-chat as `mode` and `web`.
 */

export type PartnrMode = 'fast' | 'deep';

export interface PartnrComposerPrefs {
  mode: PartnrMode;
  web: boolean;
}

export const DEFAULT_COMPOSER_PREFS: PartnrComposerPrefs = { mode: 'fast', web: true };

const storageKey = (userId: string) => `partnr:composer:${userId}`;

export function loadComposerPrefs(userId: string | null | undefined): PartnrComposerPrefs {
  if (!userId) return DEFAULT_COMPOSER_PREFS;
  try {
    const raw = JSON.parse(localStorage.getItem(storageKey(userId)) || 'null') as Partial<PartnrComposerPrefs> | null;
    return {
      mode: raw?.mode === 'deep' || raw?.mode === 'fast' ? raw.mode : DEFAULT_COMPOSER_PREFS.mode,
      web: typeof raw?.web === 'boolean' ? raw.web : DEFAULT_COMPOSER_PREFS.web,
    };
  } catch {
    return DEFAULT_COMPOSER_PREFS;
  }
}

export function saveComposerPrefs(userId: string | null | undefined, prefs: PartnrComposerPrefs): void {
  if (!userId) return;
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(prefs));
  } catch {
    // Private mode or full storage: the choice just won't stick
  }
}

// ---------------------------------------------------------------------------
// Partnr 2 composer: model, effort and web, under a new key so the old panel
// keeps reading its own. Contract section 6.
// ---------------------------------------------------------------------------

export type PartnrEffort = 'low' | 'medium' | 'high';

export interface PartnrComposerPrefsV2 {
  /** 'auto' or a registry id (src/lib/partnr/models.ts); the server falls back to Auto for anything else */
  model: string;
  effort: PartnrEffort;
  web: boolean;
}

export const DEFAULT_COMPOSER_PREFS_V2: PartnrComposerPrefsV2 = { model: 'auto', effort: 'low', web: true };

const storageKeyV2 = (userId: string) => `partnr:composer:v2:${userId}`;

const isEffort = (v: unknown): v is PartnrEffort => v === 'low' || v === 'medium' || v === 'high';

/** v2 prefs; on first read, carries over v1's Web toggle and maps Deep to High (Fast to Low). */
export function loadComposerPrefsV2(userId: string | null | undefined): PartnrComposerPrefsV2 {
  if (!userId) return DEFAULT_COMPOSER_PREFS_V2;
  try {
    const raw = JSON.parse(localStorage.getItem(storageKeyV2(userId)) || 'null') as Partial<PartnrComposerPrefsV2> | null;
    if (raw) {
      return {
        model: typeof raw.model === 'string' && raw.model ? raw.model : DEFAULT_COMPOSER_PREFS_V2.model,
        effort: isEffort(raw.effort) ? raw.effort : DEFAULT_COMPOSER_PREFS_V2.effort,
        web: typeof raw.web === 'boolean' ? raw.web : DEFAULT_COMPOSER_PREFS_V2.web,
      };
    }
    if (localStorage.getItem(storageKey(userId)) == null) return DEFAULT_COMPOSER_PREFS_V2;
    const v1 = loadComposerPrefs(userId);
    return { model: 'auto', effort: v1.mode === 'deep' ? 'high' : 'low', web: v1.web };
  } catch {
    return DEFAULT_COMPOSER_PREFS_V2;
  }
}

export function saveComposerPrefsV2(userId: string | null | undefined, prefs: PartnrComposerPrefsV2): void {
  if (!userId) return;
  try {
    localStorage.setItem(storageKeyV2(userId), JSON.stringify(prefs));
  } catch {
    // Private mode or full storage: the choice just won't stick
  }
}
